import { createCartMutation } from './createCartMutation'
import { cartLinesRemoveMutation } from './cartLinesRemove'
import { addCartLinesMutation } from './addCartLines'
import { cartBuyerIdentityUpdateMutation } from './cartBuyerIdentityUpdate'
import { updateCartLinesMutation } from './updateCartLines'
import { CART_REWARD_ATTRIBUTE_NAME } from '../../constants/rewards'
import { CART_ATTRIBUTE_UPDATE } from './cartAttributeUpdate'
import { CART_NOTE_UPDATE } from './cartNoteUpdate'
import { storeFrontApiClient as client } from '../api/apiClient'

async function getCart(cartId) {
  const getCartByIdQuery = `
          query {
    cart(
      id: "${cartId}"
    ) {
      id
      checkoutUrl
      note
      createdAt
      updatedAt
      lines(first: 50) {
        edges {
          node {
            id
            quantity
            discountAllocations {
              discountedAmount {
                amount
              }
            }
            merchandise {
              ... on ProductVariant {
                id
                title
                availableForSale
                compareAtPrice {
                  amount,
                  currencyCode
                }
                price {
                  amount
                  currencyCode
                }
                product {
                  id
                  title
                  availableForSale
                  handle
                  productType
                  featuredImage {
                    url
                    altText
                  }
                  tags
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    },
                  }
                  metafield(namespace: "custom", key: "dual_use") {
                    key
                    value
                  }
                }
              }
            }
            attributes {
              key
              value
            }
          }
        }
      }
      attributes {
        key
        value
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        totalTaxAmount {
          amount
          currencyCode
        }
        totalDutyAmount {
          amount
          currencyCode
        }
      }
      buyerIdentity {
        email
        phone
        customer {
          id
          email
        }
        countryCode
        deliveryAddressPreferences {
          ... on MailingAddress {
            address1
            address2
            city
            provinceCode
            countryCodeV2
            zip
          }
        }
      }
    }
  }
      `

  try {
    const response = await client.request(getCartByIdQuery)
    return response.data.cart
  } catch (error) {
    console.error(`Error creating cart: ${error}`)
  }
}

async function createCart(lines = [], currentMarket = 'GB') {
  try {
    const countryCode = currentMarket ?? 'GB'
    const response = await client.request(createCartMutation, {
      variables: {
        lines,
        countryCode,
      },
    })

    const cartCreate = response?.data?.cartCreate

    if (!cartCreate) {
      const apiError = response?.errors?.map((error) => error.message).join('; ')
      console.error(`Error creating cart: ${apiError || 'cartCreate missing in response'}`)
      return { error: apiError || 'Failed to create cart', data: null }
    }

    if (cartCreate.userErrors?.length) {
      const { cart } = cartCreate

      return {
        data: await undoCart(cart.id, [], cart?.lines?.edges),
        error: cartCreate.userErrors[0]?.message,
      }
    }

    return {
      error: null,
      data: cartCreate.cart,
    }
  } catch (error) {
    console.error(`Error creating cart: ${error}`)
    return { error: error?.message || String(error), data: null }
  }
}

async function addLineItem(cartId, lines) {
  try {
    const prevCart = await getCart(cartId)

    const response = await client.request(addCartLinesMutation, {
      variables: {
        cartId,
        lines,
      },
    })

    if (response.data.cartLinesAdd?.userErrors?.length) {
      return {
        error: response.data.cartLinesAdd?.userErrors[0]?.message,
        data: await undoCart(
          cartId,
          prevCart.lines.edges,
          response.data.cartLinesAdd.cart?.lines?.edges
        ),
      }
    }

    return {
      error: null,
      data: response.data.cartLinesAdd.cart,
    }
  } catch (error) {
    console.error(`Error deleting line item: ${error}`)
    throw error
  }
}

async function removeLineItem(cartId, lineIds) {
  try {
    const response = await client.request(cartLinesRemoveMutation, {
      variables: {
        cartId,
        lineIds,
      },
    })

    if (response.data.cartLinesRemove?.userErrors?.length) {
      return {
        error: response.data.cartLinesRemove?.userErrors[0]?.message,
        data: response.data.cartLinesRemove.cart,
      }
    }

    return {
      error: null,
      data: response.data.cartLinesRemove.cart,
    }
  } catch (error) {
    console.error(`Error deleting line item: ${error}`)
    throw error
  }
}

async function updateLineItem(cartId, lines) {
  try {
    const response = await client.request(updateCartLinesMutation, {
      variables: {
        cartId,
        lines,
      },
    })

    if (response.data.cartLinesUpdate?.userErrors?.length) {
      const message = response.data.cartLinesUpdate?.userErrors[0]?.message
      return {
        error: message,
        data: response.data.cartLinesUpdate.cart,
      }
    }

    return {
      error: null,
      data: response.data.cartLinesUpdate.cart,
    }
  } catch (error) {
    console.error(`Error updating line item: ${error}`)
    throw error
  }
}

async function updateCartRewardAttribute({ cartId, productIdArr, points }) {
  try {
    const response = await client.request(CART_ATTRIBUTE_UPDATE, {
      variables: {
        attrKey: CART_REWARD_ATTRIBUTE_NAME,
        cartId,
        attributes: [
          {
            key: CART_REWARD_ATTRIBUTE_NAME,
            value: JSON.stringify({ points, productIdArr }),
          },
        ],
      },
    })

    if (response.data?.cartAttributesUpdate?.userErrors?.length) {
      const message = response.data.cartAttributesUpdate?.userErrors[0]?.message
      return {
        error: message,
        data: response.data?.cartAttributesUpdate?.cart,
      }
    }

    return {
      error: null,
      data: response.data?.cartAttributesUpdate?.cart?.attribute,
    }
  } catch (err) {
    console.error(`Error adding cart reward attribute: ${err}`)
    throw err
  }
}

async function updateCartNote({ cartId, note }) {
  try {
    const response = await client.request(CART_NOTE_UPDATE, {
      variables: {
        cartId,
        note,
      },
    })

    if (response.data?.cartNoteUpdate?.userErrors?.length) {
      const message = response.data.cartNoteUpdate?.userErrors[0]?.message
      return {
        error: message,
        data: response.data?.cartNoteUpdate?.cart,
      }
    }

    return {
      error: null,
      note: response.data?.cartNoteUpdate?.cart?.note,
    }
  } catch (err) {
    console.error(`Error adding cart reward attribute: ${err}`)
    throw err
  }
}

async function undoCart(cartId, prevLines, resultLines) {
  const linesToUndo = resultLines?.map((line) => line.node.id) || []

  const removed = await removeLineItem(cartId, linesToUndo)

  if (prevLines?.length) {
    const added = await addLineItem(
      cartId,
      prevLines.map((line) => ({
        merchandiseId: line.node.merchandise.id,
        quantity: line.node.quantity,
        attributes: line.node.attributes,
      }))
    )

    return added.data
  }

  return removed.data
}

async function buyerIdentityUpdate(buyerIdentity, cartId) {
  try {
    const response = await client.request(cartBuyerIdentityUpdateMutation, {
      variables: {
        buyerIdentity,
        cartId,
      },
    })

    const payload = response?.data?.cartBuyerIdentityUpdate

    if (!payload) {
      const errors = Array.isArray(response?.errors)
        ? response.errors.map((e) => e.message).join('; ')
        : response?.errors?.message || ''

      console.error(
        `Error buyerIdentityUpdate: ${errors || 'cartBuyerIdentityUpdate missing in response'}`
      )
      return { error: errors || 'Failed to update buyer identity', data: null }
    }

    const userErrors = Array.isArray(payload.userErrors) ? payload.userErrors : []

    if (userErrors.length) {
      const message = userErrors.map((e) => e.message).join('; ')
      console.error(`Error buyerIdentityUpdate: ${message}`)
      return { error: message, data: null }
    }

    return { error: null, data: payload.cart }
  } catch (error) {
    console.error(`Error buyerIdentityUpdate: ${error}`)
    return { error: error?.message || String(error), data: null }
  }
}

export const cartClient = {
  createCart,
  getCart,
  removeLineItem,
  addLineItem,
  updateLineItem,
  buyerIdentityUpdate,
  updateCartRewardAttribute,
  updateCartNote,
}
